import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { 
  MapPin, 
  Users, 
  Book, 
  Calendar, 
  Award,
  Building2,
  GraduationCap,
  Shield,
  BookOpen,
  Globe,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react'
import { emailUtils } from '../../utils/contactUtils'
import OptimizedImage from '../../components/Image/OptimizedImage';

interface CaseStudyMetadata {
  id: string
  category: 'university' | 'public' | 'k12' | 'corporate' | 'medical' | 'special'
  icon: React.ComponentType<any>
  metricKeys: string[]
}

// 案例元数据（不需要翻译的内容）
const caseStudyMetadata: CaseStudyMetadata[] = [
  {
    id: 'guizhouTcmUniversity',
    category: 'university',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'shenzhenMoscowUniversity',
    category: 'university',
    icon: Globe,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'jdSmartValleyReadingStation',
    category: 'corporate',
    icon: Building2,
    metricKeys: ['dailyVisitors', 'borrowingVolume', 'readingSeats']
  },
  {
    id: 'xinxingCountyLibrary',
    category: 'public',
    icon: BookOpen,
    metricKeys: ['totalBooks', 'borrowingVolume', 'readingSeats', 'dailyVisitors']
  },
  {
    id: 'beijingTcmShenzhenHospital',
    category: 'medical',
    icon: Shield,
    metricKeys: ['dailyVisitors', 'borrowingVolume', 'readingSeats']
  },
  {
    id: 'wanghuaGuangmingElementary',
    category: 'k12',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'dailyVisitors', 'borrowingVolume', 'readingSeats']
  },
  {
    id: 'hunanProvincialLibrary',
    category: 'public',
    icon: BookOpen,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'malaysiaEastLibrary',
    category: 'public',
    icon: Globe,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'hongkongUniversityLibrary',
    category: 'university',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'newOrientalStaffLibrary',
    category: 'corporate',
    icon: Building2,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'shenzhenSecondLibrary',
    category: 'public',
    icon: BookOpen,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  // K-12 学校图书馆案例
  {
    id: 'beijingExperimentalSchool',
    category: 'k12',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'shanghaiInternationalSchool',
    category: 'k12',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'guangzhouHighSchool',
    category: 'k12',
    icon: GraduationCap,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  // 企业图书馆案例
  {
    id: 'alibabaStaffLibrary',
    category: 'corporate',
    icon: Building2,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },

  // 医疗机构图书馆案例
  {
    id: 'beijingUnionMedicalLibrary',
    category: 'medical',
    icon: Shield,
    metricKeys: ['totalBooks', 'physicalBooks', 'digitalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  },
  {
    id: 'shanghaiChildrensHospitalLibrary',
    category: 'medical',
    icon: Shield,
    metricKeys: ['totalBooks', 'readingSeats', 'dailyVisitors', 'borrowingVolume']
  }
]

const caseGalleryPhotos = [
  { src: '/images/cases/WechatIMG3676b9720f906e90ac3533f83708ae88.jpeg', alt: '案例图片1' },
  { src: '/images/cases/WechatIMG298.jpg', alt: '案例图片2' },
  { src: '/images/cases/WechatIMG24307.jpg', alt: '案例图片3' },
  { src: '/images/cases/WechatIMG259.jpg', alt: '案例图片4' },
  { src: '/images/cases/WechatIMG23210.jpg', alt: '案例图片5' },
  { src: '/images/cases/WechatIMG23201.jpg', alt: '案例图片6' },
  { src: '/images/cases/WechatIMG165652fd7dc6dcd7a3ad63b5ab8e6cfa.jpeg', alt: '案例图片7' },
  { src: '/images/cases/WechatIMG144.jpg', alt: '案例图片8' },
  { src: '/images/cases/WechatIMG1057.jpg', alt: '案例图片9' },
  { src: '/images/cases/887b57564ea02ab7f635ac03bc8dfc3.png', alt: '案例图片10' },
  { src: '/images/cases/7b15b1e001961c5f2eb9d9f444f960b.jpg', alt: '案例图片11' },
  { src: '/images/cases/6921739869486_.pic.jpg', alt: '案例图片12' },
  { src: '/images/cases/612641b67c3c9859c45de7bc7f6c6ff.jpg', alt: '案例图片13' },
  { src: '/images/cases/5d49f784014a94df5b162718e6730908_.jpg', alt: '案例图片14' },
  { src: '/images/cases/4f76bd9faaea0d5862af40ea9971d51f_.jpg', alt: '案例图片15' },
  { src: '/images/cases/4aee6e48eb69d2115528eb2fe817b5df.jpg', alt: '案例图片16' },
  { src: '/images/cases/3a0dbe327c42563614fedea7d0e97ad.jpg', alt: '案例图片17' },
  { src: '/images/cases/2b9ce185281963f6283aac34453252c.jpg', alt: '案例图片18' },
  { src: '/images/cases/2075c0eb52ee21416132fd5436df8b4.jpg', alt: '案例图片19' },
  { src: '/images/cases/191b1e42bf6caa1557f2ebcb3e05cd1.jpg', alt: '案例图片20' },
  { src: '/images/cases/06720ffedc69ca29c477f0186256140.jpg', alt: '案例图片21' },
  { src: '/images/cases/01068c0c71979ab328ad0d05120d76b.jpg', alt: '案例图片22' },
];

const Cases: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [caseCurrentSlide, setCaseCurrentSlide] = useState(0);
  const [caseSelectedImageIndex, setCaseSelectedImageIndex] = useState<number | null>(null);
  const isRTL = i18n.language === 'ar';

  // 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCaseCurrentSlide((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(timer);
  }, [caseGalleryPhotos.length]);
     // 无缝循环 - 调整为两张图片显示
   useEffect(() => {
     const maxSlide = caseGalleryPhotos.length - 2; // 减去2，因为一次显示两张
     if (caseCurrentSlide >= maxSlide) {
       const timer = setTimeout(() => {
         setCaseCurrentSlide(0);
       }, 500);
       return () => clearTimeout(timer);
     }
     if (caseCurrentSlide < 0) {
       const timer = setTimeout(() => {
         setCaseCurrentSlide(maxSlide);
       }, 500);
       return () => clearTimeout(timer);
     }
   }, [caseCurrentSlide, caseGalleryPhotos.length]);
  const nextCaseSlide = () => setCaseCurrentSlide((prev) => prev + 1);
  const prevCaseSlide = () => setCaseCurrentSlide((prev) => prev - 1);
  const openCaseImagePreview = (index: number) => setCaseSelectedImageIndex(index);
  const closeCaseImagePreview = () => setCaseSelectedImageIndex(null);
  const nextCaseImage = () => {
    if (caseSelectedImageIndex !== null) {
      setCaseSelectedImageIndex((caseSelectedImageIndex + 1) % caseGalleryPhotos.length);
    }
  };
  const prevCaseImage = () => {
    if (caseSelectedImageIndex !== null) {
      setCaseSelectedImageIndex((caseSelectedImageIndex - 1 + caseGalleryPhotos.length) % caseGalleryPhotos.length);
    }
  };


  const categoryFilters = [
    { key: 'all', label: t('pages.cases.filters.all'), icon: Award },
    { key: 'university', label: t('pages.cases.filters.university'), icon: GraduationCap },
    { key: 'public', label: t('pages.cases.filters.public'), icon: BookOpen },
    { key: 'k12', label: t('pages.cases.filters.k12'), icon: Book },
    { key: 'corporate', label: t('pages.cases.filters.corporate'), icon: Building2 },
    { key: 'medical', label: t('pages.cases.filters.medical'), icon: Shield }
  ]

  // 从翻译文件动态生成案例研究
  const caseStudies = caseStudyMetadata.map(metadata => {
    const caseData = t(`pages.cases.caseStudies.${metadata.id}`, { returnObjects: true }) as any
    
    return {
      ...metadata,
      ...caseData,
      metrics: metadata.metricKeys.reduce((acc: any, key: string) => {
        if (caseData.metrics && caseData.metrics[key]) {
          acc[key] = caseData.metrics[key]
        }
        return acc
      }, {})
    }
  })

  const filteredCases = selectedCategory === 'all' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === selectedCategory)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              {t('pages.cases.hero.title')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-blue-100 mb-8"
            >
              {t('pages.cases.hero.subtitle')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-blue-200 max-w-3xl mx-auto"
            >
              {t('pages.cases.hero.description')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categoryFilters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setSelectedCategory(filter.key)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  selectedCategory === filter.key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <filter.icon size={20} />
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {filteredCases.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <div className="p-8 lg:p-12">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-8">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                        <study.icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                          {study.title}
                        </h2>
                        <p className="text-blue-600 font-medium text-lg">
                          {study.subtitle}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center space-x-1">
                            <MapPin size={14} />
                            <span>{t('pages.cases.details.location')}: {study.location}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>{t('pages.cases.details.established')}: {study.establishedYear}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-8">
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {study.description}
                    </p>
                  </div>

                  {/* Metrics */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('pages.cases.details.metrics')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {Object.entries(study.metrics).map(([key, value]) => (
                        <div key={key} className="text-center">
                          <div className="text-xl font-bold text-blue-600">{String(value)}</div>
                          <div className="text-xs text-gray-600">
                            {t(`pages.cases.metricsLabels.${key}`) !== `pages.cases.metricsLabels.${key}` 
                              ? t(`pages.cases.metricsLabels.${key}`)
                              : key.replace(/([A-Z])/g, ' $1').trim()
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Three Columns: Challenges, Solutions, Results */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Challenges */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-red-600 text-sm">!</span>
                        </div>
                        {t('pages.cases.details.challenges')}
                      </h3>
                      <div className="space-y-3">
                        {study.challenges?.map((challenge: string, idx: number) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-gray-700 text-sm leading-relaxed">{challenge}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Solutions */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-sm">✓</span>
                        </div>
                        {t('pages.cases.details.solutions')}
                      </h3>
                      <div className="space-y-3">
                        {study.solutions?.map((solution: string, idx: number) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-gray-700 text-sm leading-relaxed">{solution}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Results */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-green-600 text-sm">★</span>
                        </div>
                        {t('pages.cases.details.results')}
                      </h3>
                      <div className="space-y-3">
                        {study.results?.map((result: string, idx: number) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-gray-700 text-sm leading-relaxed">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

             {/* 案例相册轮播区块 */}
       <section className="py-16 bg-white">
         <div className="container mx-auto px-4">
           <h2 className="text-3xl font-bold mb-8 text-center">{t('pages.cases.gallery.title')}</h2>
           <div className="relative flex items-center justify-center">
             <button 
               onClick={isRTL ? nextCaseSlide : prevCaseSlide} 
               className={`absolute ${isRTL ? 'right-0' : 'left-0'} z-10 p-2 bg-white rounded-full shadow hover:bg-gray-100`}
             >
               <ChevronLeft className={`${isRTL ? 'rotate-180' : ''}`} />
             </button>
             <div className="overflow-hidden w-full max-w-5xl">
               <div 
                 className={`flex transition-transform duration-500 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`} 
                 style={{ 
                   transform: isRTL 
                     ? `translateX(${caseCurrentSlide * 50}%)`
                     : `translateX(-${caseCurrentSlide * 50}%)`
                 }}
               >
                 {caseGalleryPhotos.map((img, idx) => (
                   <div key={img.src} className="flex-shrink-0 w-1/2 px-2 cursor-pointer" onClick={() => openCaseImagePreview(idx)}>
                     <OptimizedImage src={img.src} alt={img.alt} className="rounded-xl shadow-lg object-cover w-full h-80" />
                   </div>
                 ))}
               </div>
             </div>
             <button 
               onClick={isRTL ? prevCaseSlide : nextCaseSlide} 
               className={`absolute ${isRTL ? 'left-0' : 'right-0'} z-10 p-2 bg-white rounded-full shadow hover:bg-gray-100`}
             >
               <ChevronRight className={`${isRTL ? 'rotate-180' : ''}`} />
             </button>
           </div>
                     {/* 图片预览模态框 */}
           {caseSelectedImageIndex !== null && (
             <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
               <button className={`absolute top-8 ${isRTL ? 'left-8' : 'right-8'} text-white text-3xl hover:text-gray-300 z-10`} onClick={closeCaseImagePreview}><X /></button>
               <button className={`absolute ${isRTL ? 'right-8' : 'left-8'} text-white text-3xl hover:text-gray-300 z-10`} onClick={isRTL ? nextCaseImage : prevCaseImage}><ChevronLeft className={`${isRTL ? 'rotate-180' : ''}`} /></button>
               <div className="max-w-[90vw] max-h-[90vh] flex justify-center items-center">
                 <OptimizedImage 
                   src={caseGalleryPhotos[caseSelectedImageIndex].src} 
                   alt={caseGalleryPhotos[caseSelectedImageIndex].alt} 
                   className="rounded-xl shadow-2xl max-w-full max-h-full object-contain" 
                 />
               </div>
               <button className={`absolute ${isRTL ? 'left-8' : 'right-8'} text-white text-3xl hover:text-gray-300 z-10`} onClick={isRTL ? prevCaseImage : nextCaseImage}><ChevronRight className={`${isRTL ? 'rotate-180' : ''}`} /></button>
             </div>
           )}
        </div>
      </section>

      {/* Global Partnership Invitation */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Globe className="w-16 h-16 mx-auto mb-6 text-blue-200" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('pages.cases.global.partnership.title')}
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                {t('pages.cases.global.partnership.subtitle')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
            >
              <div className="text-center">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <h3 className="text-xl font-semibold mb-2">{t('pages.cases.global.partnership.institutional.title')}</h3>
                <p className="text-blue-100">
                  {t('pages.cases.global.partnership.institutional.description')}
                </p>
              </div>
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <h3 className="text-xl font-semibold mb-2">{t('pages.cases.global.partnership.integrators.title')}</h3>
                <p className="text-blue-100">
                  {t('pages.cases.global.partnership.integrators.description')}
                </p>
              </div>
              <div className="text-center">
                <Globe className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <h3 className="text-xl font-semibold mb-2">{t('pages.cases.global.partnership.distributors.title')}</h3>
                <p className="text-blue-100">
                  {t('pages.cases.global.partnership.distributors.description')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <p className="text-lg text-blue-100">
                <strong>{t('pages.cases.global.partnership.collaboration')}</strong> 
                {t('pages.cases.global.partnership.mission')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center space-x-2"
                  onClick={() => emailUtils.openConsultationEmail(t)}
                >
                  <Mail size={20} />
                  <span>{t('pages.cases.global.partnership.buttons.partnership')}</span>
                </button>
              </div>
              
              <div className="text-blue-200 text-sm space-y-2">
                <p>{t('pages.cases.global.partnership.support.languages')}</p>
                <p>{t('pages.cases.global.partnership.support.global')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">{t('pages.cases.global.contact.title')}</h3>
            <p className="text-gray-300 mb-6">
              {t('pages.cases.global.contact.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm">
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <span>+86 400-996-7738</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <button 
                  onClick={() => emailUtils.openDirectEmail('rfidlib@libmj.com')}
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer transition-colors duration-200"
                  title="发送邮件到 rfidlib@libmj.com"
                >
                  rfidlib@libmj.com
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <Globe size={16} />
                <span>{t('pages.cases.global.contact.global_partnerships')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Cases 